// Modelo de la bandeja de la mesa de ayuda: funciones puras (sin estado ni
// efectos) que deciden qué se pide al backend, qué fila se ve y qué cuentan los
// contadores del rail. Viven fuera del hook para poder probarlas sin montar el
// inbox y para que la vista nueva aplique exactamente las mismas reglas.

import { accountKeyOf } from "../../../layouts/helpdeskLayout/HelpdeskRail";
import { conversationDisplayName } from "../../../components/helpdesk/ConversationList";
import { channelMatchesFilter } from "../../../services/aiChatService";
import { isPinnable } from "../../../services/helpdeskPinsService";

// ── Transformador: AI log → shape de conversacion ────────────────────────────
export function mapAILogToConv(log) {
  return {
    id: log.id,
    conversation_id: log.conversation_id || null,
    user_id: log.user_id,
    user_role: log.user_role,
    company_id: log.company_id,
    logistic_company_id: log.logistic_company_id,
    status: "ai",
    channel: "ai",
    transfer_reason: log.was_transferred ? "ai_failure" : null,
    created_at: log.created_at,
    last_message_at: log.created_at,
    last_message_preview: log.first_user_message,
    package_id: log.package_id,
    sla_breached: false,
    assigned_to: null,
    was_transferred: log.was_transferred,
  };
}

// ¿La conversación pertenece a la cuenta filtrada? Filtro cliente-side mientras
// el backend no acepte el parámetro; sin channel_account no hay coincidencia.
// El channel_account de la conversación solo trae {label, phone, instance_name}
// —nunca `id`—, así que además de la clave normalizada se comparan los campos
// sueltos: la clave del rail pudo derivarse de cualquiera de ellos.
export function conversationMatchesAccount(conv, accountKey) {
  const account = conv?.channel_account;
  if (!account || !accountKey) return false;
  if (accountKeyOf(account) === accountKey) return true;
  return [account.id, account.instance_name, account.phone, account.label].some(
    (value) => value && value === accountKey
  );
}

// El backend proyecta los datos de la conversación PLANOS en cada resultado
// (contact_name / channel / external_id) mientras que el contrato del servicio
// los anida bajo `conversation`. Se leen ambas formas para no depender de cuál
// esté desplegada: sin `channel` correcto, loadDetail enruta al proxy de Portal
// y el detalle de una conversación de WhatsApp/Messenger falla en silencio.
export function searchResultConversation(result) {
  const nested = result?.conversation ?? {};
  return {
    contact_name: nested.contact_name ?? result?.contact_name ?? null,
    channel: nested.channel ?? result?.channel ?? undefined,
    external_id: nested.external_id ?? result?.external_id ?? null,
  };
}

// Identidad del mensaje para la key de React: el backend devuelve `id`, el
// contrato nombra el campo `message_id`. El último respaldo evita keys
// duplicadas si faltaran ambos.
export function searchResultKey(result, index) {
  return (
    result?.id ||
    result?.message_id ||
    `${result?.conversation_id ?? "conv"}-${result?.created_at ?? index}`
  );
}

// ── Mapeo: clave del rail → filtros de fetch + filtro cliente-side ──────────
//
// El rail es la única fuente de filtros de estado/canal del inbox. Cada clave
// define:
//   - statusFilter/channelFilter: parámetros que viajan al backend (fetch).
//   - clientFilter: predicado adicional aplicado en memoria (para keys que el
//     back aún no segmenta de forma directa, ej. "mis casos", "vencidos", "grupos").
//
// TODO(back): endpoint /helpdesk/counters para contadores globales. Por ahora
// los contadores se derivan de la lista cargada (`conversations`), por lo que
// reflejan "lo que estás viendo" según la sub-vista actual, no totales globales.
/** Prefijo del inboxKey de la vista de un número retirado. */
export const RETIRED_KEY_PREFIX = "retired.";

export function inboxKeyToBaseFilters(key) {
  // Carpeta activa: 'folder.<id>'. El id viaja al fetch como folder_id; el
  // resto de filtros base queda vacío (la carpeta ES el filtro).
  if (key.startsWith("folder.")) {
    return { statusFilter: "", channelFilter: "", folderId: key.slice(7) };
  }
  // Número retirado: 'retired.<id de la cuenta>'. Sus conversaciones no están
  // en ninguna otra bandeja; el id viaja al fetch como retired_account_id.
  if (key.startsWith(RETIRED_KEY_PREFIX)) {
    return {
      statusFilter: "",
      channelFilter: "",
      retiredAccountId: key.slice(RETIRED_KEY_PREFIX.length),
    };
  }
  switch (key) {
    case "inbox.all":
      return { statusFilter: "", channelFilter: "" };
    case "inbox.unassigned":
      return { statusFilter: "pending", channelFilter: "" };
    case "inbox.mine":
      return { statusFilter: "assigned", channelFilter: "" };
    case "inbox.sla":
      return { statusFilter: "", channelFilter: "" };
    case "inbox.ai":
      // Historial: conversaciones donde la IA respondió o derivó en los
      // últimos 30 días, resueltas incluidas (el servidor ignora `status`).
      return { statusFilter: "", channelFilter: "", aiState: "handled" };
    case "inbox.ai_live":
      // Monitoreo: lo que la IA BYO está respondiendo AHORA.
      return { statusFilter: "", channelFilter: "", aiState: "active" };
    case "inbox.resolved":
      return { statusFilter: "resolved", channelFilter: "" };
    case "channel.whatsapp":
      return { statusFilter: "", channelFilter: "whatsapp" };
    case "channel.messenger":
      return { statusFilter: "", channelFilter: "messenger" };
    case "channel.instagram":
      return { statusFilter: "", channelFilter: "instagram" };
    case "channel.portal":
      return { statusFilter: "", channelFilter: "portal" };
    case "channel.groups":
      return { statusFilter: "", channelFilter: "" };
    // Canal interno (asistente de gerencia): el backend lo segmenta por el
    // mismo parámetro `channel` que el resto de canales omnicanal.
    case "channel.internal":
      return { statusFilter: "", channelFilter: "internal" };
    default:
      return { statusFilter: "pending", channelFilter: "" };
  }
}

export function inboxKeyToClientFilter(key, userId) {
  // Vista de carpeta: el predicado cliente-side hace que lo MOVIDO FUERA (o
  // resuelto) desaparezca al instante cuando llega el SSE, sin refetch.
  if (key.startsWith("folder.")) {
    const folderId = key.slice(7);
    return (c) =>
      c.channel !== "internal" && c.folder_id === folderId && c.status !== "resolved";
  }
  const base = (() => {
    switch (key) {
      case "inbox.mine":
        return (c) => c.assigned_to && c.assigned_to === userId;
      case "inbox.sla":
        return (c) => !!c.sla_breached;
      case "channel.groups":
        return (c) => !!c.is_group;
      case "inbox.ai":
        // Rastro permanente de la IA: no se apaga al derivar ni al resolver,
        // así que un SSE de la conversación no la expulsa de la bandeja.
        return (c) => !!c.ai_last_activity_at;
      case "inbox.ai_live":
        // Expulsión en vivo: el SSE de asignación/escalamiento/resolve llega
        // con byo_ai_active=false y la fila sale sin refetch.
        return (c) => !!c.byo_ai_active && !c.assigned_to;
      default:
        return () => true;
    }
  })();

  // "Resueltos" es el archivo: es la ÚNICA bandeja que muestra casos cerrados.
  // En todas las demás se ocultan aunque el backend los devuelva (varias vistas
  // piden sin filtro de estado), para que lo pendiente no se mezcle con lo ya
  // cerrado — que era justo lo que ensuciaba la bandeja.
  //
  // El canal interno (asistente de gerencia) es una consulta interna, no un caso
  // de atención: si el backend lo devuelve en la unión, se descarta acá para que
  // no aparezca en ninguna bandeja por defecto (incluidas las carpetas, mismo
  // criterio que channelMatchesFilter en el SSE). Solo entra en su bandeja.
  //
  // "Atendidas por IA" también muestra resueltas: es el historial de lo que la
  // IA atendió, y el backend (ai_state=handled) las devuelve a propósito.
  const inScope = (c) => key === "channel.internal" || c.channel !== "internal";
  if (key === "inbox.resolved" || key === "inbox.ai") {
    return (c) => inScope(c) && base(c);
  }
  return (c) => inScope(c) && c.status !== "resolved" && base(c);
}

// channelMatchesFilter (¿el evento SSE pertenece al filtro de canal activo?)
// vive en aiChatService: es la misma regla que aplica el listado, y ahí se puede
// probar sin montar el inbox.

// ¿La fila quedó fuera del filtro de comercio? El servidor ya filtra por
// effective_commerce_id, pero lo que entra sin pasar por el fetch (el SSE de
// conversaciones nuevas y las reaperturas) llegaba de cualquier comercio. Se
// replica la MISMA regla del fetch, y solo donde el fetch la aplica: filas
// omnicanal (el portal y los logs de IA se piden sin el filtro y no traen el
// campo) y fuera de la vista de un número retirado (ahí el filtro ES el número).
function isOutsideCommerceFilter(conv, commerceFilter, inboxKey) {
  if (!commerceFilter?.id) return false;
  if (inboxKey.startsWith(RETIRED_KEY_PREFIX)) return false;
  if (!conv.channel || conv.channel === "portal" || conv.channel === "ai") return false;
  return String(conv.effective_commerce_id ?? "") !== String(commerceFilter.id);
}

// ── Filtrado cliente-side (rail sub-view + búsqueda + commerce) ─────────────
/**
 * Filas que la bandeja muestra: predicado de la clave del rail, cuenta elegida
 * en el sub-árbol, comercio filtrado y búsqueda local; en las vistas de
 * triage, además, el orden de más antiguo a más nuevo. No muta `conversations`.
 * @param {object[]} conversations
 * @param {object} options
 * @param {{id: string, name?: string}|null} [options.commerceFilter]
 * @returns {object[]}
 */
export function filterVisibleConversations(
  conversations,
  { clientFilter, accountFilter, commerceFilter, searchQuery, getDisplayName, inboxKey, t }
) {
  const q = searchQuery.trim().toLowerCase();
  // Búsqueda por número: se compara también en dígitos puros, para que
  // "56912345678" encuentre un contact_phone guardado como "+56 9 1234 5678".
  const qDigits = q.replace(/\D/g, "");
  const filtered = conversations.filter((c) => {
    if (!clientFilter(c)) return false;
    if (accountFilter && !conversationMatchesAccount(c, accountFilter)) return false;
    if (isOutsideCommerceFilter(c, commerceFilter, inboxKey)) return false;
    if (q) {
      // Mismo nombre que pinta la fila (incluye "Equipo interno" en el canal
      // interno): buscar tiene que encontrar lo que el usuario ve.
      const name = conversationDisplayName(c, getDisplayName, t).toLowerCase();
      const phone = (c.contact_phone || c.external_id || "").toLowerCase();
      const phoneDigits = phone.replace(/\D/g, "");
      const preview = (c.last_message_preview || "").toLowerCase();
      // Ticket del caso vigente: "#123" y "123" lo encuentran igual (los
      // dígitos se comparan exactos para no chocar con la búsqueda de teléfono,
      // que es por subcadena).
      const ticket =
        c.ticket_number != null ? String(c.ticket_number) : "";
      if (
        !name.includes(q) &&
        !phone.includes(q) &&
        !(qDigits && phoneDigits.includes(qDigits)) &&
        !(qDigits && ticket === qDigits) &&
        !preview.includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  // En las vistas de triage (Sin asignar / Vencidos SLA) ordenamos por
  // antigüedad: el caso más viejo primero, para que el agente ataque lo que
  // lleva más tiempo esperando. Reordenamiento puramente cliente-side sobre
  // los datos ya cargados; no toca el fetch del inbox.
  if (inboxKey === "inbox.unassigned" || inboxKey === "inbox.sla") {
    filtered.sort((a, b) => {
      const ageA = new Date(a.created_at || a.last_message_at || 0).getTime();
      const ageB = new Date(b.created_at || b.last_message_at || 0).getTime();
      return ageA - ageB; // ascendente = más antiguo primero
    });
  }

  return filtered;
}

// ── Contadores del rail (calculados sobre la lista cargada) ────────────────
// TODO(back): endpoint /helpdesk/counters para totales globales.
/**
 * @returns {{all:number, unassigned:number, mine:number, sla:number,
 *   ai_live:number, resolved:number, whatsapp:number, messenger:number,
 *   instagram:number, portal:number, groups:number, internal:number}}
 */
export function computeInboxCounters(conversations, userId) {
  // Los resueltos solo cuentan en su propia bandeja: si sumaran en el resto,
  // los contadores dirían "50" mientras la lista muestra 12 sin cerrar.
  const resolved = conversations.filter((x) => x.status === "resolved");
  // Mismo criterio con el canal interno: el render lo descarta de toda bandeja
  // que no sea la suya (inboxKeyToClientFilter), así que contarlo acá volvería
  // a separar el badge de lo que se ve.
  const c = conversations.filter(
    (x) => x.status !== "resolved" && x.channel !== "internal"
  );
  // Internos SIN resolver: el nombre dice qué cuenta (no son "los resueltos
  // del canal interno", que además no se contarían acá).
  const internalConversationsCount = conversations.filter(
    (x) => x.channel === "internal" && x.status !== "resolved"
  );
  return {
    all: c.length,
    unassigned: c.filter((x) => x.status === "pending").length,
    mine: c.filter((x) => x.assigned_to && x.assigned_to === userId).length,
    sla: c.filter((x) => x.sla_breached).length,
    // "Atendidas por IA" sin contador: no hay endpoint de conteo por bandeja
    // y la lista cargada en otras vistas no trae el historial de 30 días
    // (con resueltas) que define la bandeja; un número derivado sería falso.
    // "IA en curso" refleja lo cargado en pantalla, igual que el resto de
    // counters del rail (limitación documentada: TODO(back) /helpdesk/counters).
    ai_live: c.filter((x) => x.byo_ai_active && !x.assigned_to).length,
    resolved: resolved.length,
    whatsapp: c.filter(
      (x) => x.channel === "whatsapp" || x.channel === "whatsapp_unofficial"
    ).length,
    messenger: c.filter((x) => x.channel === "messenger").length,
    instagram: c.filter((x) => x.channel === "instagram").length,
    portal: c.filter((x) => x.channel === "portal" || !x.channel).length,
    groups: c.filter((x) => x.is_group).length,
    // Canal interno: solo se carga con su bandeja activa, así que en el resto
    // de vistas el contador queda en 0 (misma limitación que el resto: los
    // contadores salen de la lista cargada).
    internal: internalConversationsCount.length,
  };
}

// ── Conversaciones fijadas (pin por usuario) ────────────────────────────────
// El pin es POR USUARIO y vive en el microservicio conversations (no en el
// portal ni en los logs de IA, que son otra colección). El tope lo aplica el
// backend; acá solo se decide qué se puede fijar y cómo se ordena la lista.
// isPinnable vive en el servicio (la lista la usa sin importar este modelo,
// que a su vez importa la lista) y se reexporta para los consumidores del modelo.
export { isPinnable };

/**
 * Normaliza la respuesta de GET /conversations/pins a Map<id, pinnedAtMs>.
 * pinnedAtMs = Date.parse(pinned_at) si es válido; si falta o es inválido, 0.
 * Ignora entradas sin id string no vacío. Si un id se repite, gana el pinned_at
 * mayor. Formas aceptadas: pins[], conversation_ids[], ids[], array de strings
 * u objetos. Cualquier otra cosa (null, objeto sin esas claves, número) → Map
 * vacío.
 * @param {unknown} payload
 * @returns {Map<string, number>}
 */
export function normalizePins(payload) {
  const pins = new Map();
  if (payload == null) return pins;

  let entries;
  if (Array.isArray(payload)) {
    entries = payload;
  } else if (typeof payload === "object") {
    if (Array.isArray(payload.pins)) entries = payload.pins;
    else if (Array.isArray(payload.conversation_ids)) entries = payload.conversation_ids;
    else if (Array.isArray(payload.ids)) entries = payload.ids;
    else return pins;
  } else {
    return pins;
  }

  entries.forEach((entry) => {
    const id =
      typeof entry === "string" ? entry : entry?.conversation_id ?? entry?.id;
    if (typeof id !== "string" || !id) return;
    const parsed = Date.parse(entry?.pinned_at);
    const pinnedAtMs = Number.isNaN(parsed) ? 0 : parsed;
    const current = pins.get(id);
    // Empate no reemplaza: cualquier valor equivalente sirve para el orden.
    if (current === undefined || pinnedAtMs > current) pins.set(id, pinnedAtMs);
  });

  return pins;
}

/**
 * Pone primero las fijadas (pinnedAtMs desc; empate: conserva el orden
 * relativo de entrada) y deja el resto en su orden original. No muta `list`.
 * Devuelve la MISMA referencia `list` si no hay ninguna fijada en ella (evita
 * renders de más).
 * @param {object[]} list
 * @param {Map<string, number>} pins
 * @returns {object[]}
 */
export function sortPinnedFirst(list, pins) {
  if (!Array.isArray(list) || !pins || pins.size === 0) return list;
  if (!list.some((conv) => pins.has(conv?.id))) return list;

  const pinned = [];
  const rest = [];
  list.forEach((conv) => {
    if (pins.has(conv?.id)) pinned.push(conv);
    else rest.push(conv);
  });
  // El sort de JS es estable: los empates conservan el orden de entrada.
  pinned.sort((a, b) => (pins.get(b.id) ?? 0) - (pins.get(a.id) ?? 0));
  return [...pinned, ...rest];
}

/**
 * Para una fijada que NO vino en la página cargada (se trajo por el detalle):
 * ¿la habría devuelto el servidor con los filtros base de la bandeja? Replica
 * lo que el fetch filtra en el servidor y el clientFilter no cubre.
 * @param {object} conv
 * @param {{statusFilter?: string, channelFilter?: string, folderId?: string,
 *   aiState?: string, retiredAccountId?: string}} baseFilters
 * @returns {boolean}
 */
export function matchesBaseFilters(conv, baseFilters) {
  if (!conv) return false;
  const { statusFilter, channelFilter, folderId, aiState, retiredAccountId } =
    baseFilters || {};

  // El detalle no dice de qué número es: no se puede afirmar que pertenezca a
  // la vista de un número retirado.
  if (retiredAccountId) return false;
  // Las fijadas son omnicanal: el portal vive en otro servicio.
  if (channelFilter === "portal") return false;
  if (channelFilter) {
    if (!channelMatchesFilter(conv.channel, channelFilter)) return false;
  } else if (conv.channel === "internal") {
    // El canal interno solo aparece en su propia bandeja.
    return false;
  }

  if (
    (statusFilter === "pending" ||
      statusFilter === "assigned" ||
      statusFilter === "resolved") &&
    conv.status !== statusFilter
  ) {
    return false;
  }
  if (folderId && conv.folder_id !== folderId) return false;
  if (aiState === "active" && !conv.byo_ai_active) return false;
  if (aiState === "handled" && !conv.ai_last_activity_at) return false;

  return true;
}

/**
 * Une la lista cargada con las fijadas traídas aparte (`extras`, Map<id, conv>)
 * que no están en ella y que pasan matchesBaseFilters. Las extras se agregan al
 * FINAL (sortPinnedFirst las sube después). Una extra cuyo id ya está en
 * `conversations` se ignora (gana la fila de la lista). Una extra cuyo id ya no
 * está en `pins` se ignora. Devuelve `conversations` (misma referencia) si no
 * agrega nada.
 * @param {object[]} conversations
 * @param {Map<string, object>} extras
 * @param {Map<string, number>} pins
 * @param {object} baseFilters
 * @returns {object[]}
 */
export function withPinnedExtras(conversations, extras, pins, baseFilters) {
  if (!conversations || !extras || extras.size === 0) return conversations;

  const present = new Set(conversations.map((conv) => conv?.id));
  const added = [];
  extras.forEach((conv, id) => {
    if (present.has(id)) return;
    if (!pins?.has(id)) return;
    if (!matchesBaseFilters(conv, baseFilters)) return;
    present.add(id);
    added.push(conv);
  });

  if (added.length === 0) return conversations;
  return [...conversations, ...added];
}
